import { call, put, takeEvery, select } from 'redux-saga/effects';
import { message } from 'antd';
import {
    aiSummarySuccess,
    aiSummaryFailure,
    aiQuestionSuccess,
    aiQuestionFailure,
    aiIncrementRequestCount,
    aiResetHourlyCount,
    aiResetDailyCount,
} from './aiActions';
import { fetchAiSummary, fetchAiAnswer, isQuestionAppropriate, MODERATION_ERROR } from './aiService';
import { HOURLY_LIMIT, DAILY_LIMIT } from './aiReducer';

const {
    AI_SUMMARY_REQUEST,
    AI_QUESTION_REQUEST,
} = require('./aiActions').constants;

const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * 60 * 60 * 1000;

// Helper to get value from either Immutable Record or plain object
const getValue = (obj, key) => {
    if (!obj) return undefined;
    if (typeof obj.get === 'function') {
        return obj.get(key);
    }
    return obj[key];
};

function* checkAndResetRateLimits() {
    const ai = yield select(state => state.ai);
    const now = Date.now();

    const lastHourlyReset = getValue(ai, 'lastHourlyReset') || now;
    const lastDailyReset = getValue(ai, 'lastDailyReset') || now;

    // Reset hourly count if an hour has passed
    if (now - lastHourlyReset >= ONE_HOUR) {
        yield put(aiResetHourlyCount());
    }

    // Reset daily count if a day has passed
    if (now - lastDailyReset >= ONE_DAY) {
        yield put(aiResetDailyCount());
    }
}

function* checkRateLimits() {
    yield* checkAndResetRateLimits();

    const ai = yield select(state => state.ai);
    const hourlyCount = getValue(ai, 'hourlyRequestCount') || 0;
    const dailyCount = getValue(ai, 'dailyRequestCount') || 0;

    if (hourlyCount >= HOURLY_LIMIT) {
        throw new Error('Hourly limit reached (10 requests/hour). Please try again later.');
    }

    if (dailyCount >= DAILY_LIMIT) {
        throw new Error('Daily limit reached (50 requests/day). Please try again tomorrow.');
    }
}

/**
 * Get user-friendly error message from error object
 * @param {Error} e - Error object
 * @param {string} defaultMessage - Default message if no specific error matched
 * @returns {string} - User-friendly error message
 */
const getErrorMessage = (e, defaultMessage) => {
    // Timeout errors
    if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        return 'Request timed out. Please try again.';
    }

    // Rate limit errors (client-side)
    if (e.message?.includes('limit')) {
        return e.message;
    }

    // Backend rate limiting (HTTP 429)
    if (e.response?.status === 429) {
        return 'Too many requests. Please wait a moment and try again.';
    }

    // Unauthorized (HTTP 401)
    if (e.response?.status === 401) {
        return 'Please log in to use AI features.';
    }

    // Service unavailable (HTTP 503)
    if (e.response?.status === 503) {
        return 'AI service is temporarily unavailable. Please try again later.';
    }

    // Backend error (HTTP 502)
    if (e.response?.status === 502) {
        return 'AI service error. Please try again later.';
    }

    // Moderation error
    if (e.message?.includes('cannot be processed')) {
        return e.message;
    }

    // Backend error response with message
    if (e.response?.data?.message) {
        return e.response.data.message;
    }

    // Generic error with message
    if (e.message && e.message !== 'Network Error') {
        return e.message;
    }

    return defaultMessage;
};

function* getSummary(action) {
    try {
        yield* checkRateLimits();
        yield put(aiIncrementRequestCount());

        const response = yield call(fetchAiSummary, action.payload);
        yield put(aiSummarySuccess(response));
    } catch (e) {
        const errorMessage = getErrorMessage(e, 'Unable to get summary. Please try again.');
        message.error(errorMessage);
        yield put(aiSummaryFailure(errorMessage));
    }
}

function* askQuestion(action) {
    try {
        // Content moderation check (client-side pre-check)
        const { question } = action.payload;
        if (!isQuestionAppropriate(question)) {
            message.warning(MODERATION_ERROR);
            yield put(aiQuestionFailure(MODERATION_ERROR));
            return;
        }

        yield* checkRateLimits();
        yield put(aiIncrementRequestCount());

        const response = yield call(fetchAiAnswer, action.payload);
        yield put(aiQuestionSuccess({
            id: Date.now(),
            question: action.payload.question,
            answer: response,
        }));
    } catch (e) {
        const errorMessage = getErrorMessage(e, 'Unable to get answer. Please try again.');
        message.error(errorMessage);
        yield put(aiQuestionFailure(errorMessage));
    }
}

export default function* aiSaga() {
    yield* [
        takeEvery(AI_SUMMARY_REQUEST, getSummary),
        takeEvery(AI_QUESTION_REQUEST, askQuestion),
    ];
}
